import React, { useState } from "react";
import { Modal, Icon } from "./common";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

const ICONS = ["Target", "BookOpen", "Star", "Trophy", "PenLine", "Puzzle", "Palette", "Rocket"];

export default function ChallengeFormModal({ open, onClose, onDone }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title_ar: "", title_en: "", desc_ar: "", desc_en: "",
    icon: "Target", points: 15, requires_approval: true,
    start_date: today, end_date: nextWeek,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title_ar.trim()) { toast.error(t("validation.required")); return; }
    if (form.end_date < form.start_date) { toast.error(t("validation.end_before_start")); return; }
    setBusy(true);
    try {
      await api.post("/staff/challenges", { ...form, points: Number(form.points) });
      toast.success(t("teacher.challenge_created"));
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("teacher.create_challenge")} testId="challenge-form-modal" wide>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.name_ar")} *</label>
          <input data-testid="challenge-title-ar" className="input-magic" dir="rtl" value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.name_en")}</label>
          <input data-testid="challenge-title-en" className="input-magic" dir="ltr" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-sm font-extrabold block mb-1">{t("admin.desc_ar")}</label>
          <textarea data-testid="challenge-desc-ar" className="input-magic min-h-[70px]" dir="rtl" value={form.desc_ar} onChange={(e) => set("desc_ar", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-sm font-extrabold block mb-1">{t("admin.desc_en")}</label>
          <textarea data-testid="challenge-desc-en" className="input-magic min-h-[70px]" dir="ltr" value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("challenges.reward")}</label>
          <input data-testid="challenge-points" type="number" min="1" max="100" className="input-magic" value={form.points} onChange={(e) => set("points", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("admin.icon")}</label>
          <div className="flex gap-1.5 flex-wrap">
            {ICONS.map((ic) => (
              <button key={ic} type="button" data-testid={`challenge-icon-${ic}`} onClick={() => set("icon", ic)}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${form.icon === ic ? "bg-[#7625D8]/40 border-[#9C42F5] glow-purple" : "bg-[#0D0B38]/50 border-white/12 hover:border-white/30"}`}>
                <Icon name={ic} className="w-5 h-5" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("challenges.not_started")} — {t("admin.date")}</label>
          <input data-testid="challenge-start" type="date" className="input-magic" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-extrabold block mb-1">{t("challenges.ended")} — {t("admin.date")}</label>
          <input data-testid="challenge-end" type="date" className="input-magic" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
        </div>
        <label className="sm:col-span-2 flex items-center gap-3 rounded-xl bg-[#0D0B38]/50 border border-white/10 px-4 py-3 cursor-pointer">
          <input data-testid="challenge-approval-toggle" type="checkbox" checked={form.requires_approval}
            onChange={(e) => set("requires_approval", e.target.checked)} className="w-5 h-5 accent-[#9C42F5]" />
          <span className="text-sm font-bold">{t("challenges.requires_approval")}</span>
        </label>
      </div>
      <button data-testid="challenge-submit-btn" disabled={busy} onClick={submit} className="btn-magic w-full mt-5">
        {busy ? t("common.loading") : t("teacher.create_challenge")}
      </button>
    </Modal>
  );
}
